import React, { useCallback, useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { useHistory, useLocation } from 'react-router-dom';

import CustomLoading from '../../../util_components/custom_loading';
import ClampText from '../shared/clamp_text';
import CodeBreaks from '../shared/code_breaks';
import DataTable from '../shared/data_table';
import { What } from '../shared/cells';
import LoadFailed from '../shared/load_failed';
import { FilterBar, SelectFilter } from '../shared/controls';
import LookupPicker from '../additional_payments/lookup_picker';
import { useHeadingRefocus } from '../shared/use_heading_refocus';
import { toggle_sort } from '../shared/use_sortable';
import { replace_search } from '../shared/url_query';
import { LIMITS } from '../additional_payments/params';
import { fetch_additional_teachers } from '../api';
import { month_label } from '../format';
import { fetch_audit_csv, fetch_audit_facets, fetch_audit_list } from './api';
import './audit.css';
import {
  append_page,
  audit_error_view,
  audit_facets_view,
  audit_page_view,
  is_long_text_field,
  teacher_name_from_rows,
  when_parts
} from './adapters';
import {
  audit_csv_fallback_name,
  audit_csv_params,
  audit_facets_params,
  audit_filter_summary,
  audit_filters_on,
  audit_list_params,
  blank_audit_filters,
  clear_audit_filters,
  has_audit_scope,
  audit_search,
  parse_audit_url,
  prune_to_facets,
  with_scope
} from './params';

/* audit-trail browser over teacher_payment_audit (/teacher-payments/audit, a hidden tab). */
const NO_FACETS = { entity_types: [], actions: [], actors: [] };

// The URL's scope wins (no month with a teacher = any month); else the shell's month. The narrowing
// picks apply either way.
const initial_filters = (link, pay_month) => {
  const picks = { entity_type: link.entity_type, action: link.action, actor: link.actor };
  const scope = { pay_month: link.pay_month, teacher_uuid: link.teacher_uuid };
  if (has_audit_scope(scope)) return { ...blank_audit_filters(), ...scope, ...picks };
  return { ...blank_audit_filters(pay_month), ...picks };
};

// Field / old → new pairs: a key column and its value at every width (audit.css .tp-au-changes).
const ChangeList = ({ changes }) =>
  changes.length ? (
    <dl className="tp-au-changes">
      {changes.map((c, i) => (
        <React.Fragment key={`${c.field}-${i}`}>
          <dt className="tp-mono">
            {c.field}
            <span className="sr-only">: </span>
          </dt>
          <dd>
            {/* Free text clamps at 3 lines with Show more (full text stays in the DOM); values never do. */}
            {is_long_text_field(c.field) ? (
              <ClampText className="tp-au-note" lines={3}>
                {c.old_text} → {c.new_text}
              </ClampText>
            ) : (
              <>
                <CodeBreaks text={c.old_text} /> → <CodeBreaks text={c.new_text} />
              </>
            )}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  ) : (
    <span className="tp-hint">No field changes recorded</span>
  );

const AuditTrailTab = ({ pay_month = '', months = [], on_error }) => {
  const history = useHistory();
  const location = useLocation();
  const [link] = useState(() => parse_audit_url(location.search));
  const [filters, setFilters] = useState(() => initial_filters(link, pay_month));
  const [facets, setFacets] = useState(NO_FACETS);
  const [facets_error, setFacetsError] = useState('');
  const [rows, setRows] = useState([]);
  const [next_before_id, setNextBeforeId] = useState(null);
  const [next_offset, setNextOffset] = useState(null);
  // null = newest first (keyset, before_id); {key, dir} = a column sort, paged by offset instead.
  const [sort, setSort] = useState(link.sort);
  const [list_status, setListStatus] = useState('idle'); // idle | loading | ready | error
  const [list_error, setListError] = useState('');
  const [loading_more, setLoadingMore] = useState(false);
  const [more_error, setMoreError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [csv_error, setCsvError] = useState('');
  const [facets_rev, setFacetsRev] = useState(0);
  const [list_rev, setListRev] = useState(0);
  // Only the latest list/facets read may land; a slow earlier one must not overwrite it.
  const list_seq = useRef(0);
  const facets_seq = useRef(0);
  const heading_ref = useRef(null);
  const on_focus = useHeadingRefocus(heading_ref);

  // The shell's month arrives after its /months read; adopt it only while nothing is scoped yet.
  useEffect(() => {
    setFilters((f) =>
      has_audit_scope(f) ? f : { ...f, pay_month: blank_audit_filters(pay_month).pay_month }
    );
  }, [pay_month]);

  // Filters and sort mirror into the URL (replace: a filter is not a history step).
  useEffect(() => {
    replace_search(history, location.pathname, (search) => audit_search(search, filters, sort));
  }, [filters, sort, history, location.pathname, location.search]);

  // Returns the inline text, or '' once a permission failure was handed to the shell.
  const inline_error = useCallback(
    (e, fallback) => {
      const v = audit_error_view(e, fallback);
      if (v.kind === 'permission') {
        on_error(e, fallback);
        return '';
      }
      return v.text;
    },
    [on_error]
  );

  const scoped = has_audit_scope(filters);
  const { pay_month: scope_month, teacher_uuid: scope_teacher } = filters;

  // Facets depend on the scope only; entity/action/actor picks never re-read them.
  useEffect(() => {
    facets_seq.current += 1;
    const mine = facets_seq.current;
    setFacets(NO_FACETS);
    setFacetsError('');
    if (!scoped) return;
    fetch_audit_facets(audit_facets_params({ pay_month: scope_month, teacher_uuid: scope_teacher }))
      .then((data) => {
        if (mine !== facets_seq.current) return;
        const next = audit_facets_view(data);
        setFacets(next);
        setFilters((f) => prune_to_facets(f, next));
      })
      .catch((e) => {
        if (mine !== facets_seq.current) return;
        const text = inline_error(e, 'Could not load the filter values.');
        if (text) setFacetsError(text);
      });
  }, [scope_month, scope_teacher, scoped, facets_rev, inline_error]);

  useEffect(() => {
    list_seq.current += 1;
    const mine = list_seq.current;
    setRows([]);
    setNextBeforeId(null);
    setNextOffset(null);
    setListError('');
    setMoreError('');
    // An older page still in flight belongs to the old filters; its reply is dropped (list_seq).
    setLoadingMore(false);
    if (!scoped) {
      setListStatus('idle');
      return;
    }
    setListStatus('loading');
    fetch_audit_list(audit_list_params(filters, { sort }))
      .then((data) => {
        if (mine !== list_seq.current) return;
        const page = audit_page_view(data);
        setRows(page.rows);
        setNextBeforeId(page.next_before_id);
        setNextOffset(page.next_offset);
        setListStatus('ready');
      })
      .catch((e) => {
        if (mine !== list_seq.current) return;
        setListError(inline_error(e, 'Could not load the audit trail.'));
        setListStatus('error');
      });
  }, [filters, scoped, list_rev, inline_error, sort]);

  // Unsorted pages by before_id (keyset); a column sort pages by offset instead — never both.
  const load_more = () => {
    if (loading_more || (!sort && !next_before_id) || (sort && next_offset === null)) return;
    const mine = list_seq.current;
    setLoadingMore(true);
    setMoreError('');
    fetch_audit_list(
      audit_list_params(filters, { before_id: next_before_id, sort, offset: next_offset })
    )
      .then((data) => {
        if (mine !== list_seq.current) return;
        const page = audit_page_view(data);
        setRows((cur) => append_page(cur, page));
        setNextBeforeId(page.next_before_id);
        setNextOffset(page.next_offset);
        setLoadingMore(false);
      })
      .catch((e) => {
        if (mine !== list_seq.current) return;
        setMoreError(inline_error(e, 'Could not load older entries.'));
        setLoadingMore(false);
      });
  };

  const apply_sort = (key) => {
    setSort((s) => toggle_sort(s, key));
  };

  const download_csv = () => {
    if (downloading || !scoped) return;
    setDownloading(true);
    setCsvError('');
    fetch_audit_csv(audit_csv_params(filters))
      .then(({ csv, file_name }) => {
        if (!csv) {
          setCsvError('The CSV export came back empty. Try again.');
        } else {
          saveAs(
            new Blob([csv], { type: 'text/csv;charset=utf-8' }),
            file_name || audit_csv_fallback_name(filters)
          );
        }
        setDownloading(false);
      })
      .catch((e) => {
        setCsvError(inline_error(e, 'Could not download the audit trail.'));
        setDownloading(false);
      });
  };

  const set_filter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const search_teachers = useCallback(
    (q) => fetch_additional_teachers(q).then((d) => (Array.isArray(d.rows) ? d.rows : [])),
    []
  );

  const month_options = months.map((m) => ({ value: m.pay_month, label: m.label }));
  if (filters.pay_month && !month_options.some((m) => m.value === filters.pay_month)) {
    month_options.unshift({ value: filters.pay_month, label: month_label(filters.pay_month) });
  }

  const teacher_text =
    filters.teacher_name ||
    teacher_name_from_rows(rows, filters.teacher_uuid) ||
    (filters.teacher_uuid ? `Teacher ${filters.teacher_uuid.slice(-8)}` : '');

  // Three tracks: when · teacher, reason and record · changes. Record, Action and By sort through
  // the Sort select (their values sit in the teacher track).
  const columns = [
    {
      key: 'when',
      label: 'When',
      sort_key: 'time',
      track: { compact: '104px', full: '112px' },
      render: (r) => (
        <div className="tp-c-who">
          {/* Date over time: the track is narrow and a timestamp never wraps mid-value. */}
          <div className="tp-au-when">
            {when_parts(r.when).map((part, i) => (
              <React.Fragment key={part}>
                {i ? ' ' : null}
                <span>{part}</span>
              </React.Fragment>
            ))}
          </div>
          <div className="tp-c-meta">{r.month}</div>
        </div>
      )
    },
    {
      // Teacher and record share one track so Changes gets the width.
      key: 'teacher',
      role: 'identifier',
      label: 'Teacher · Record',
      sort_key: 'teacher_name',
      sorts: [
        { sort_key: 'entity', label: 'Record' },
        { sort_key: 'action', label: 'Action' },
        { sort_key: 'actor', label: 'By' }
      ],
      track: { compact: '180px', full: '200px' },
      render: (r) => (
        <What
          title={r.teacher_name || <span className="tp-muted">Not teacher-specific</span>}
          lines={[
            r.reason ? (
              <ClampText lines={2}>
                <CodeBreaks text={r.reason} />
              </ClampText>
            ) : null,
            <span className="tp-au-rec">
              {r.entity_label}
              {r.entity_id ? (
                <>
                  {' · '}
                  <span className="tp-mono tp-au-id">{r.entity_id}</span>
                </>
              ) : null}
            </span>,
            `${r.action} · by ${r.actor}`
          ]}
        />
      )
    },
    {
      key: 'changes',
      label: 'Changes',
      render: (r) => <ChangeList changes={r.changes} />
    }
  ];

  const csv_off = downloading || !scoped;
  const default_month = blank_audit_filters(pay_month).pay_month;
  const summary =
    list_status === 'ready'
      ? audit_filter_summary({
          count: rows.length,
          has_more: sort ? next_offset !== null : !!next_before_id,
          filters,
          month_text: filters.pay_month ? month_label(filters.pay_month) : '',
          teacher_text,
          facets,
          default_month,
          loading: false
        })
      : null;

  return (
    <div className="tp-au" onFocus={on_focus}>
      <FilterBar
        label="Audit trail filters"
        summary={
          summary ? (
            <>
              Showing <b>{summary.lead}</b>
              {summary.rest}
            </>
          ) : null
        }
      >
        <SelectFilter
          id="tp-au-month"
          label="Month"
          aria_label="Filter by month"
          value={filters.pay_month}
          all_label="Any month"
          options={month_options}
          active={filters.pay_month !== default_month}
          onChange={(v) => setFilters((f) => with_scope(f, { pay_month: v }))}
        />
        <div className="tp-fsearch">
          <LookupPicker
            id="tp-audit-teacher"
            name="teacher filter"
            label="Teacher"
            label_hidden
            placeholder="Filter by name, slug or email"
            min_chars={LIMITS.TEACHER_QUERY_MIN_LEN}
            value_text={teacher_text}
            search={search_teachers}
            option_key={(t) => t.teacher_uuid}
            render_option={(t) => (
              <span>
                <b>{t.name}</b> · {t.email}
              </span>
            )}
            on_pick={(t) =>
              setFilters((f) =>
                with_scope(f, { teacher_uuid: t.teacher_uuid, teacher_name: t.name })
              )
            }
            on_clear={() =>
              setFilters((f) => with_scope(f, { teacher_uuid: '', teacher_name: '' }))
            }
          />
        </div>
        <SelectFilter
          id="tp-au-entity"
          label="Record"
          aria_label="Filter by record type"
          value={filters.entity_type}
          all_label="All record types"
          options={facets.entity_types}
          onChange={(v) => set_filter('entity_type', v)}
        />
        <SelectFilter
          id="tp-au-action"
          label="Action"
          aria_label="Filter by action"
          value={filters.action}
          all_label="All actions"
          options={facets.actions}
          onChange={(v) => set_filter('action', v)}
        />
        <SelectFilter
          id="tp-au-actor"
          label="By"
          aria_label="Filter by who made the change"
          value={filters.actor}
          all_label="Anyone"
          options={facets.actors}
          onChange={(v) => set_filter('actor', v)}
        />
        {audit_filters_on(filters, default_month) ? (
          <button
            type="button"
            className="tp-link tp-fbar-end"
            onClick={() => setFilters(clear_audit_filters(pay_month))}
          >
            Clear filters
          </button>
        ) : null}
      </FilterBar>

      {facets_error ? (
        <LoadFailed text={facets_error} on_retry={() => setFacetsRev((r) => r + 1)} />
      ) : null}
      {csv_error ? (
        <div className="tp-banner tp-banner-error" role="alert">
          {csv_error}
        </div>
      ) : null}

      <div className="tp-card tp-au-list">
        <div className="tp-card-hd">
          <span className="tp-card-hd-main">
            <span
              className="tp-card-hd-title"
              role="heading"
              aria-level={2}
              tabIndex={-1}
              ref={heading_ref}
            >
              Audit trail
            </span>
            <span className="tp-card-hd-sub">
              Every recorded change to teacher payments, newest first
            </span>
          </span>
          <span className="tp-card-hd-acts">
            <button
              type="button"
              className="tp-btn-out"
              onClick={download_csv}
              aria-disabled={csv_off ? 'true' : undefined}
            >
              {downloading ? 'Preparing…' : 'Download CSV'}
            </button>
          </span>
        </div>
        {!scoped ? (
          <div className="tp-empty tp-au-inset">
            Choose a month or a teacher to see the audit trail.
          </div>
        ) : null}
        {list_status === 'loading' ? <CustomLoading /> : null}
        {list_status === 'error' && list_error ? (
          <LoadFailed text={list_error} on_retry={() => setListRev((r) => r + 1)} />
        ) : null}
        {list_status === 'ready' ? (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(r) => r.id}
            emptyText="No audit entries match these filters."
            sort={sort}
            on_sort={apply_sort}
            on_sort_set={setSort}
            card_below={660}
          />
        ) : null}

        {list_status === 'ready' && more_error ? (
          <div className="tp-banner tp-banner-error tp-au-inset" role="alert">
            {more_error}
          </div>
        ) : null}
        {list_status === 'ready' && (next_before_id || next_offset !== null) ? (
          <div className="tp-au-more">
            <button
              type="button"
              className="tp-btn-out"
              onClick={load_more}
              aria-disabled={loading_more ? 'true' : undefined}
            >
              {loading_more ? 'Loading…' : sort ? 'Load more' : 'Load older'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AuditTrailTab;
